export const ADMIN_THEME_KEYS = ['classic', 'forest', 'royal', 'sunrise', 'lagoon'] as const;

export type AdminThemeKey = typeof ADMIN_THEME_KEYS[number];

export interface AdminThemePalette {
  key: AdminThemeKey;
  label: string;
  description: string;
  primary: string;
  primaryRgb: string;
  primaryHover: string;
  navBg: string;
  navText: string;
  navHoverBg: string;
  navActiveBg: string;
  navAccent: string;
  pageBg: string;
  cardBg: string;
  cardBorder: string;
  accent: string;
  accentSoft: string;
  footerText: string;
  footerBorder: string;
}

export const DEFAULT_ADMIN_THEME: AdminThemeKey = 'forest';

export const ADMIN_THEMES: Record<AdminThemeKey, AdminThemePalette> = {
  classic: {
    key: 'classic',
    label: 'Suite241 classique',
    description: 'Bleu actuel, sobre et familier.',
    primary: '#0d6efd',
    primaryRgb: '13, 110, 253',
    primaryHover: '#0b5ed7',
    navBg: '#0d6efd',
    navText: 'rgba(255, 255, 255, 0.68)',
    navHoverBg: 'rgba(255, 255, 255, 0.08)',
    navActiveBg: 'rgba(0, 0, 0, 0.15)',
    navAccent: '#ffffff',
    pageBg: 'var(--bs-body-bg)',
    cardBg: '#ffffff',
    cardBorder: '#e5e7eb',
    accent: '#0b508c',
    accentSoft: 'rgba(13, 110, 253, 0.1)',
    footerText: '#0b508c',
    footerBorder: 'rgba(15, 80, 140, 0.22)',
  },
  forest: {
    key: 'forest',
    label: 'Forêt',
    description: 'Verts profonds avec accents frais.',
    primary: '#047857',
    primaryRgb: '4, 120, 87',
    primaryHover: '#065f46',
    navBg: '#064e3b',
    navText: 'rgba(236, 253, 245, 0.76)',
    navHoverBg: 'rgba(16, 185, 129, 0.18)',
    navActiveBg: 'rgba(4, 120, 87, 0.48)',
    navAccent: '#a7f3d0',
    pageBg: '#f1f8f4',
    cardBg: '#ffffff',
    cardBorder: '#cfe7da',
    accent: '#0f766e',
    accentSoft: 'rgba(16, 185, 129, 0.12)',
    footerText: '#065f46',
    footerBorder: 'rgba(4, 120, 87, 0.26)',
  },
  royal: {
    key: 'royal',
    label: 'Royal',
    description: 'Indigo marqué et interface élégante.',
    primary: '#4f46e5',
    primaryRgb: '79, 70, 229',
    primaryHover: '#4338ca',
    navBg: '#312e81',
    navText: 'rgba(238, 242, 255, 0.78)',
    navHoverBg: 'rgba(129, 140, 248, 0.18)',
    navActiveBg: 'rgba(79, 70, 229, 0.42)',
    navAccent: '#c7d2fe',
    pageBg: '#f5f6ff',
    cardBg: '#ffffff',
    cardBorder: '#dfe3ff',
    accent: '#7c3aed',
    accentSoft: 'rgba(124, 58, 237, 0.11)',
    footerText: '#4338ca',
    footerBorder: 'rgba(79, 70, 229, 0.24)',
  },
  sunrise: {
    key: 'sunrise',
    label: 'Sunrise',
    description: 'Orange chaleureux et touches dorées.',
    primary: '#ea580c',
    primaryRgb: '234, 88, 12',
    primaryHover: '#c2410c',
    navBg: '#9a3412',
    navText: 'rgba(255, 247, 237, 0.8)',
    navHoverBg: 'rgba(251, 146, 60, 0.2)',
    navActiveBg: 'rgba(234, 88, 12, 0.44)',
    navAccent: '#fed7aa',
    pageBg: '#fff7ed',
    cardBg: '#ffffff',
    cardBorder: '#fed7aa',
    accent: '#d97706',
    accentSoft: 'rgba(245, 158, 11, 0.14)',
    footerText: '#9a3412',
    footerBorder: 'rgba(234, 88, 12, 0.24)',
  },
  lagoon: {
    key: 'lagoon',
    label: 'Lagoon',
    description: 'Bleu cyan avec accents turquoise.',
    primary: '#0891b2',
    primaryRgb: '8, 145, 178',
    primaryHover: '#0e7490',
    navBg: '#155e75',
    navText: 'rgba(236, 254, 255, 0.78)',
    navHoverBg: 'rgba(34, 211, 238, 0.18)',
    navActiveBg: 'rgba(8, 145, 178, 0.45)',
    navAccent: '#a5f3fc',
    pageBg: '#f0fdfa',
    cardBg: '#ffffff',
    cardBorder: '#bae6fd',
    accent: '#0d9488',
    accentSoft: 'rgba(20, 184, 166, 0.12)',
    footerText: '#155e75',
    footerBorder: 'rgba(8, 145, 178, 0.24)',
  },
};

export const ADMIN_THEME_OPTIONS = ADMIN_THEME_KEYS.map((key) => ADMIN_THEMES[key]);

export const isAdminThemeKey = (value: unknown): value is AdminThemeKey => (
  typeof value === 'string' && (ADMIN_THEME_KEYS as readonly string[]).includes(value)
);

export const getAdminTheme = (value: unknown): AdminThemePalette => {
  return isAdminThemeKey(value) ? ADMIN_THEMES[value] : ADMIN_THEMES[DEFAULT_ADMIN_THEME];
};

export const adminThemeStyle = (value: unknown): Record<string, string> => {
  const theme = getAdminTheme(value);

  return {
    '--bs-primary': theme.primary,
    '--bs-primary-rgb': theme.primaryRgb,
    '--bs-link-color': theme.primary,
    '--bs-link-hover-color': theme.primaryHover,
    '--suite241-admin-primary': theme.primary,
    '--suite241-admin-primary-rgb': theme.primaryRgb,
    '--suite241-admin-primary-hover': theme.primaryHover,
    '--suite241-admin-nav-bg': theme.navBg,
    '--suite241-admin-nav-text': theme.navText,
    '--suite241-admin-nav-hover-bg': theme.navHoverBg,
    '--suite241-admin-nav-active-bg': theme.navActiveBg,
    '--suite241-admin-nav-accent': theme.navAccent,
    '--suite241-page-bg': theme.pageBg,
    '--suite241-card-bg': theme.cardBg,
    '--suite241-card-border': theme.cardBorder,
    '--suite241-admin-accent': theme.accent,
    '--suite241-admin-accent-soft': theme.accentSoft,
    '--suite241-admin-footer-text': theme.footerText,
    '--suite241-admin-footer-border': theme.footerBorder,
  };
};
